import { CommonDialogs } from "@mcleod/common";
import { Component, DataSource, DesignerInterface, Label, Layout, Panel, ResourceFileProps, TabProps, TableCell, serializeComponents } from "@mcleod/components";
import { Collection } from "@mcleod/core";
import { ResourceDesignerTab } from "../common/ResourceDesignerTab";
import { AbstractUIDesigner } from "./AbstractUIDesigner";
import { ComponentIdValidationResult, ComponentIdValidator, IdValidationResult } from "./ComponentIdValidator";
import { DesignerActionExecutor } from "./DesignerActionExecutor";
import { DesignerDataSourcePanel } from "./DesignerDataSourcePanel";
import { PropertiesTablePanel } from "./PropertiesTablePanel";

export class LayoutDesignerTab extends ResourceDesignerTab {
    designer: DesignerInterface;
    lastSelectedDataSource: DataSource;
    dataSources: Collection<DataSource> = {};
    designerPanel: Layout;
    dataSourcePanel: DesignerDataSourcePanel;
    private hasIdValidationErrors: boolean = false;
    private validationPreventedSave: boolean = false;
    private idValidator: ComponentIdValidator;
    private _panelProps: PropertiesTablePanel;
    actionExecutor: DesignerActionExecutor;

    constructor(designer: DesignerInterface, props: ResourceFileProps, tabProps?: Partial<TabProps>) {
        super("layout", props, tabProps);
        this.actionExecutor = new DesignerActionExecutor(designer);
        this.designer = designer;
        this.designerPanel = new Layout({
            fillHeight: true,
            fillRow: true,
            _designer: designer,
            scrollY: true,
            allowDecoratorOverride: true,
            applyFieldLevelPermissions: false,
            applyFieldLevelLicensing: false
        });

        this.add(this.designerPanel);

        if (designer instanceof AbstractUIDesigner) {
            this.dataSourcePanel = new DesignerDataSourcePanel(this, designer);
            this.add(this.dataSourcePanel);
        }
        this.idValidator = new ComponentIdValidator(this);
    }

    get propsPanel(): PropertiesTablePanel {
        if (!this._panelProps)
            this._panelProps = new PropertiesTablePanel(this.designer);
        return this._panelProps;
    }

    getSearchRequestEndpointAndBody(): [string, any] {
        if (!(this.designer instanceof AbstractUIDesigner))
            return null;

        const filter: any = {
            ...this.getSearchRequestBody(),
            apply_field_level_perms: false,
            apply_field_level_licensing: false,
            apply_field_level_company_type: false,
            apply_field_level_ltl_type: false,
            apply_field_level_experiment: false,
            apply_field_level_multi_currency: false
        };
        return [this.designer.endpointPath, filter];
    }

    dataSourceIdUpdated(dataSource: DataSource, oldId: string, newId: string): void {
        delete this.dataSources[oldId];
        this.dataSources[newId] = dataSource;
        const children = this.dataSourcePanel.getDesignerDataSourceRecursiveChildren();
        for (let j = 0; j < children.length; j++) {
            const currentChild = children[j];
            if (currentChild instanceof Label && currentChild.text === oldId)
                currentChild.text = newId;
        }
    }

    override updateFromServerResponse(serverResponse: any) {
        super.updateFromServerResponse(serverResponse);
        this.validationPreventedSave = false;
        this.hasIdValidationErrors = false;
        this.idValidator.updateUI();
    }

    async validateSave(): Promise<boolean> {
        const result = this.validateComponentIdsIfNeeded(true);
        if (result.hasErrors()) {
            const panel = new Panel();
            panel.add(new Label({ text: "Please resolve the following errors and try again." }));
            result.getUniqueErrors().forEach(error => panel.add(new Label({ text: `\t\u2022 ${error}` })));
            await CommonDialogs.showDialog(panel, { title: "Unable to Save Layout" });
            this.savePrevented(result, result.errors[0].component);
            return false;
        } else if (result.hasWarnings()) {
            const msg = `This layout contains ${result.warnings.length} auto-generated component ids. Would you like to review these warnings or continue saving?`;
            const saveLayout = !await CommonDialogs.showYesNo(msg, "Warnings Found", {
                xVisible: false,
                yesButtonCaption: "Review Warnings",
                noButtonProps: { caption: "Continue Save", imageName: "disk" }
            });
            if (!saveLayout)
                this.savePrevented(result, result.warnings[0].component);
            return saveLayout;
        } else {
            return true;
        }
    }

    savePrevented(result: IdValidationResult, scrollToComponent: Component) {
        this.validationPreventedSave = true;
        this.designer.selectComponent(scrollToComponent);
        this.idValidator.updateUI(result, true);
        if (scrollToComponent instanceof TableCell)
            scrollToComponent = scrollToComponent.row.table;
        scrollToComponent.scrollIntoView();
    }

    async validateComponentId(component: Component): Promise<boolean> {
        let idValidationResult = this.validateComponentIdsIfNeeded();
        if (idValidationResult == null)
            idValidationResult = this.idValidator.validateComponentId(component.id);
        this.hasIdValidationErrors = idValidationResult.hasErrors();

        this.idValidator.updateUI(idValidationResult, this.validationPreventedSave);
        const componentResult: ComponentIdValidationResult = idValidationResult.errors?.find(error => error.component == component);
        const valid = componentResult?.error == null;
        if (!valid)
            await CommonDialogs.showMessage(componentResult.error);
        return Promise.resolve(valid);
    }

    validateComponentIdsIfNeeded(force?: boolean): IdValidationResult {
        if (this.hasIdValidationErrors || this.validationPreventedSave || force === true) {
            const result = this.idValidator.validateActiveTab();
            this.hasIdValidationErrors = result.hasErrors();
            return result;
        }
    }

    public getSerializedDesignerLayout(): string {
        return serializeComponents(this.designerPanel, this.dataSources);
    }
}
